import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { THEME_MODES, readThemePreference, resolveTheme, writeThemePreference } from './themePreference';

const ThemeContext = createContext(null);
const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

function getSystemPrefersDark() {
  try {
    return Boolean(window.matchMedia?.(COLOR_SCHEME_QUERY).matches);
  } catch {
    return false;
  }
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readThemePreference);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);
  const resolvedTheme = resolveTheme(mode, systemPrefersDark);

  const setMode = useCallback((nextMode) => {
    if (!THEME_MODES.includes(nextMode)) return;
    if (nextMode === 'system') setSystemPrefersDark(getSystemPrefersDark());
    writeThemePreference(nextMode);
    setModeState(nextMode);
  }, []);

  useEffect(() => {
    if (mode !== 'system') return undefined;

    let mediaQuery;
    try {
      mediaQuery = window.matchMedia?.(COLOR_SCHEME_QUERY);
    } catch {
      return undefined;
    }
    if (!mediaQuery) return undefined;

    setSystemPrefersDark(Boolean(mediaQuery.matches));
    const handleChange = (event) => setSystemPrefersDark(Boolean(event.matches));
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener?.(handleChange);
    return () => mediaQuery.removeListener?.(handleChange);
  }, [mode]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;

    const color = window.getComputedStyle(root)
      .getPropertyValue('--spark-color-theme-chrome')
      .trim();
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && color) meta.setAttribute('content', color);
  }, [resolvedTheme]);

  const value = useMemo(() => ({ mode, resolvedTheme, setMode }), [mode, resolvedTheme, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemePreference() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemePreference must be used within ThemeProvider');
  return context;
}
